import React, { useState, useEffect } from 'react';
import { Card, Table, Button, Modal, Form, Badge, Tabs, Tab } from 'react-bootstrap';
import { useAuth } from '../contexts/AuthContext';
import { wfhAPI } from '../services/api';
import { formatDateIST } from '../utils/dateFormatter';
import Swal from 'sweetalert2';
import { FaHome, FaPlus, FaCheck, FaTimes, FaCalendarAlt } from 'react-icons/fa';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import Pagination from '../components/Pagination';

const WorkFromHome = () => {
  const { isAdmin, isHR, user } = useAuth();
  const [myWFH, setMyWFH] = useState([]);
  const [pendingWFH, setPendingWFH] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({
    start_date: '',
    end_date: '',
    reason: ''
  });
  const [myWFHPage, setMyWFHPage] = useState(1);
  const [pendingWFHPage, setPendingWFHPage] = useState(1);
  const [itemsPerPage] = useState(10);

  useEffect(() => {
    fetchWFH();
  }, []);

  const fetchWFH = async () => {
    try {
      const myWFHRes = await wfhAPI.getMyWFH();
      setMyWFH(myWFHRes.data);

      if (user?.manager_id || isAdmin || isHR) {
        const pendingRes = await wfhAPI.getPendingWFH();
        setPendingWFH(pendingRes.data);
      }
    } catch (error) {
      console.error('Error fetching WFH requests:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await wfhAPI.create(formData);
      setShowModal(false);
      setFormData({ start_date: '', end_date: '', reason: '' });
      fetchWFH();
      Swal.fire({
        icon: 'success',
        title: 'Success!',
        text: 'WFH request submitted!',
        confirmButtonColor: '#635bff',
        timer: 2000,
        showConfirmButton: false
      });
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Error submitting WFH request',
        confirmButtonColor: '#635bff'
      });
    }
  };

  const handleApprove = async (id) => {
    try {
      await wfhAPI.updateStatus(id, { status: 'approved' });
      fetchWFH();
      Swal.fire({
        icon: 'success',
        title: 'Approved!',
        text: 'WFH request has been approved.',
        confirmButtonColor: '#635bff',
        timer: 2000,
        showConfirmButton: false
      });
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Error approving WFH request',
        confirmButtonColor: '#635bff'
      });
    }
  };

  const handleReject = async (id) => {
    const result = await Swal.fire({
      title: 'Reject WFH Request',
      input: 'textarea',
      inputLabel: 'Reason for rejection',
      inputPlaceholder: 'Enter reason for rejection...',
      showCancelButton: true,
      confirmButtonColor: '#635bff',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'Reject',
      inputValidator: (value) => {
        if (!value) {
          return 'Please provide a reason!';
        }
      }
    });

    if (result.isConfirmed) {
      try {
        await wfhAPI.updateStatus(id, { status: 'rejected', rejection_reason: result.value });
        fetchWFH();
        Swal.fire({
          icon: 'success',
          title: 'Rejected!',
          text: 'WFH request has been rejected.',
          confirmButtonColor: '#635bff',
          timer: 2000,
          showConfirmButton: false
        });
      } catch (error) {
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'Error rejecting WFH request',
          confirmButtonColor: '#635bff'
        });
      }
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { bg: '#fef3c7', color: '#92400e', text: 'Pending' },
      approved: { bg: '#d1fae5', color: '#065f46', text: 'Approved' },
      rejected: { bg: '#fee2e2', color: '#991b1b', text: 'Rejected' }
    };
    const config = statusConfig[status] || statusConfig.pending;
    return (
      <span style={{
        padding: '4px 12px',
        borderRadius: '20px',
        fontSize: '12px',
        fontWeight: '500',
        backgroundColor: config.bg,
        color: config.color
      }}>
        {config.text}
      </span>
    );
  };

  const [activeTab, setActiveTab] = useState('my-wfh');

  return (
    <>
      {/* Header */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '32px',
        flexWrap: 'wrap',
        gap: '16px'
      }}>
        <h1 style={{
          fontSize: '32px',
          fontWeight: '700',
          color: '#0a0a0a',
          letterSpacing: '-0.02em',
          margin: 0
        }}>
          Work From Home
        </h1>
        <button
          onClick={() => setShowModal(true)}
          style={{
            padding: '12px 24px',
            borderRadius: '8px',
            backgroundColor: '#635bff',
            color: 'white',
            border: 'none',
            fontSize: '14px',
            fontWeight: '500',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
          }}
        >
          <FaPlus size={14} />
          Apply for WFH
        </button>
      </div>

      {/* Tabs */}
      <div style={{
        marginBottom: '24px',
        borderBottom: '2px solid #e5e7eb'
      }}>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button
            onClick={() => setActiveTab('my-wfh')}
            style={{
              padding: '12px 24px',
              border: 'none',
              backgroundColor: 'transparent',
              color: activeTab === 'my-wfh' ? '#635bff' : '#6b7280',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer',
              borderBottom: activeTab === 'my-wfh' ? '2px solid #635bff' : '2px solid transparent',
              marginBottom: '-2px',
              transition: 'all 0.2s ease'
            }}
          >
            My WFH Requests
          </button>
          {pendingWFH.length > 0 && (
            <button
              onClick={() => setActiveTab('pending')}
              style={{
                padding: '12px 24px',
                border: 'none',
                backgroundColor: 'transparent',
                color: activeTab === 'pending' ? '#635bff' : '#6b7280',
                fontSize: '14px',
                fontWeight: '600',
                cursor: 'pointer',
                borderBottom: activeTab === 'pending' ? '2px solid #635bff' : '2px solid transparent',
                marginBottom: '-2px',
                transition: 'all 0.2s ease',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
            >
              Pending Approvals
              <span style={{
                padding: '2px 8px',
                borderRadius: '12px',
                backgroundColor: '#635bff',
                color: 'white',
                fontSize: '12px',
                fontWeight: '600'
              }}>
                {pendingWFH.length}
              </span>
            </button>
          )}
        </div>
      </div>

      {/* My WFH Tab */}
      {activeTab === 'my-wfh' && (
        <div style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          border: '1px solid #e5e7eb',
          boxShadow: '0 1px 2px rgba(0, 0, 0, 0.04)',
          overflow: 'hidden'
        }}>
          <div style={{
            padding: '20px 24px',
            borderBottom: '1px solid #e5e7eb',
            backgroundColor: '#faf9ff'
          }}>
            <h5 style={{
              margin: 0,
              fontSize: '18px',
              fontWeight: '600',
              color: '#0a0a0a',
              display: 'flex',
              alignItems: 'center',
              gap: '10px'
            }}>
              <FaHome style={{ color: '#635bff' }} />
              My WFH Requests
            </h5>
          </div>
          <div style={{ overflowX: 'auto' }}>
            <Table hover responsive style={{ margin: 0 }}>
              <thead style={{ backgroundColor: '#faf9ff' }}>
                <tr>
                  <th style={{
                    padding: '16px 24px',
                    fontSize: '13px',
                    fontWeight: '600',
                    color: '#6b7280',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    borderBottom: '1px solid #e5e7eb'
                  }}>Start Date</th>
                  <th style={{
                    padding: '16px 24px',
                    fontSize: '13px',
                    fontWeight: '600',
                    color: '#6b7280',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    borderBottom: '1px solid #e5e7eb'
                  }}>End Date</th>
                  <th style={{
                    padding: '16px 24px',
                    fontSize: '13px',
                    fontWeight: '600',
                    color: '#6b7280',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    borderBottom: '1px solid #e5e7eb'
                  }}>Status</th>
                  <th style={{
                    padding: '16px 24px',
                    fontSize: '13px',
                    fontWeight: '600',
                    color: '#6b7280',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    borderBottom: '1px solid #e5e7eb'
                  }}>Reason</th>
                </tr>
              </thead>
              <tbody>
                {myWFH.length === 0 ? (
                  <tr>
                    <td colSpan="4" style={{
                      padding: '60px 20px',
                      textAlign: 'center',
                      color: '#9ca3af',
                      fontSize: '14px'
                    }}>
                      <div style={{ fontSize: '48px', marginBottom: '16px' }}>🏠</div>
                      <div style={{ fontSize: '16px', fontWeight: '500', color: '#374151', marginBottom: '8px' }}>
                        No WFH requests
                      </div>
                      <div>Your work from home requests will appear here</div>
                    </td>
                  </tr>
                ) : (
                  (() => {
                    const indexOfLastItem = myWFHPage * itemsPerPage;
                    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
                    const currentItems = myWFH.slice(indexOfFirstItem, indexOfLastItem);

                    return currentItems.map((wfh) => (
                      <tr key={wfh.id} style={{ borderBottom: '1px solid #e5e7eb' }}>
                        <td style={{
                          padding: '16px 24px',
                          fontSize: '14px',
                          color: '#374151'
                        }}>
                          {formatDateIST(wfh.start_date)}
                        </td>
                        <td style={{
                          padding: '16px 24px',
                          fontSize: '14px',
                          color: '#374151'
                        }}>
                          {formatDateIST(wfh.end_date)}
                        </td>
                        <td style={{
                          padding: '16px 24px'
                        }}>
                          {getStatusBadge(wfh.status)}
                        </td>
                        <td style={{
                          padding: '16px 24px',
                          fontSize: '14px',
                          color: '#374151'
                        }}>
                          <div dangerouslySetInnerHTML={{ __html: wfh.reason }} />
                        </td>
                      </tr>
                    ));
                  })()
                )}
              </tbody>
            </Table>
          </div>
          {myWFH.length > 0 && (
            <Pagination
              currentPage={myWFHPage}
              totalPages={Math.ceil(myWFH.length / itemsPerPage)}
              onPageChange={setMyWFHPage}
              itemsPerPage={itemsPerPage}
              totalItems={myWFH.length}
            />
          )}
        </div>
      )}

      {/* Pending Approvals Tab */}
      {activeTab === 'pending' && pendingWFH.length > 0 && (
        <div style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          border: '1px solid #e5e7eb',
          boxShadow: '0 1px 2px rgba(0, 0, 0, 0.04)',
          overflow: 'hidden'
        }}>
          <div style={{
            padding: '20px 24px',
            borderBottom: '1px solid #e5e7eb',
            backgroundColor: '#faf9ff'
          }}>
            <h5 style={{
              margin: 0,
              fontSize: '18px',
              fontWeight: '600',
              color: '#0a0a0a',
              display: 'flex',
              alignItems: 'center',
              gap: '10px'
            }}>
              <FaCalendarAlt style={{ color: '#635bff' }} />
              Pending Approvals
            </h5>
          </div>
          <div style={{ overflowX: 'auto' }}>
            <Table hover responsive style={{ margin: 0 }}>
              <thead style={{ backgroundColor: '#faf9ff' }}>
                <tr>
                  <th style={{
                    padding: '16px 24px',
                    fontSize: '13px',
                    fontWeight: '600',
                    color: '#6b7280',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    borderBottom: '1px solid #e5e7eb'
                  }}>Employee</th>
                  <th style={{
                    padding: '16px 24px',
                    fontSize: '13px',
                    fontWeight: '600',
                    color: '#6b7280',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    borderBottom: '1px solid #e5e7eb'
                  }}>Start Date</th>
                  <th style={{
                    padding: '16px 24px',
                    fontSize: '13px',
                    fontWeight: '600',
                    color: '#6b7280',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    borderBottom: '1px solid #e5e7eb'
                  }}>End Date</th>
                  <th style={{
                    padding: '16px 24px',
                    fontSize: '13px',
                    fontWeight: '600',
                    color: '#6b7280',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    borderBottom: '1px solid #e5e7eb'
                  }}>Reason</th>
                  <th style={{
                    padding: '16px 24px',
                    fontSize: '13px',
                    fontWeight: '600',
                    color: '#6b7280',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    borderBottom: '1px solid #e5e7eb'
                  }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {(() => {
                  const indexOfLastItem = pendingWFHPage * itemsPerPage;
                  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
                  const currentItems = pendingWFH.slice(indexOfFirstItem, indexOfLastItem);

                  return currentItems.map((wfh) => (
                    <tr key={wfh.id} style={{ borderBottom: '1px solid #e5e7eb' }}>
                      <td style={{
                        padding: '16px 24px',
                        fontSize: '14px',
                        fontWeight: '500',
                        color: '#0a0a0a'
                      }}>
                        User #{wfh.user_id}
                      </td>
                      <td style={{
                        padding: '16px 24px',
                        fontSize: '14px',
                        color: '#374151'
                      }}>
                        {formatDateIST(wfh.start_date)}
                      </td>
                      <td style={{
                        padding: '16px 24px',
                        fontSize: '14px',
                        color: '#374151'
                      }}>
                        {formatDateIST(wfh.end_date)}
                      </td>
                      <td style={{
                        padding: '16px 24px',
                        fontSize: '14px',
                        color: '#374151'
                      }}>
                        <div dangerouslySetInnerHTML={{ __html: wfh.reason }} />
                      </td>
                      <td style={{
                        padding: '16px 24px'
                      }}>
                        <div style={{ display: 'flex', gap: '8px' }}>
                          <button
                            onClick={() => handleApprove(wfh.id)}
                            style={{
                              padding: '6px 12px',
                              borderRadius: '6px',
                              backgroundColor: '#10b981',
                              color: 'white',
                              border: 'none',
                              fontSize: '13px',
                              fontWeight: '500',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '6px'
                            }}
                          >
                            <FaCheck size={12} />
                            Approve
                          </button>
                          <button
                            onClick={() => handleReject(wfh.id)}
                            style={{
                              padding: '6px 12px',
                              borderRadius: '6px',
                              backgroundColor: '#ef4444',
                              color: 'white',
                              border: 'none',
                              fontSize: '13px',
                              fontWeight: '500',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '6px'
                            }}
                          >
                            <FaTimes size={12} />
                            Reject
                          </button>
                        </div>
                      </td>
                    </tr>
                  ));
                })()}
              </tbody>
            </Table>
          </div>
          {pendingWFH.length > 0 && (
            <Pagination
              currentPage={pendingWFHPage}
              totalPages={Math.ceil(pendingWFH.length / itemsPerPage)}
              onPageChange={setPendingWFHPage}
              itemsPerPage={itemsPerPage}
              totalItems={pendingWFH.length}
            />
          )}
        </div>
      )}

      {/* Apply for WFH Sidebar */}
      <>
        {/* Overlay */}
        {showModal && (
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              zIndex: 1040,
              transition: 'opacity 0.3s ease'
            }}
            onClick={() => setShowModal(false)}
          />
        )}

        {/* Sidebar */}
        <div
          style={{
            position: 'fixed',
            top: 0,
            right: showModal ? 0 : '-600px',
            width: '600px',
            height: '100vh',
            backgroundColor: 'white',
            boxShadow: '-2px 0 8px rgba(0, 0, 0, 0.1)',
            zIndex: 1050,
            transition: 'right 0.3s ease',
            display: 'flex',
            flexDirection: 'column',
            overflowY: 'auto'
          }}
        >
          {/* Header */}
          <div style={{
            padding: '24px',
            borderBottom: '1px solid #e5e7eb',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            backgroundColor: 'white',
            position: 'sticky',
            top: 0,
            zIndex: 10
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div style={{
                width: '40px',
                height: '40px',
                borderRadius: '8px',
                backgroundColor: '#e8e5ff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '20px'
              }}>
                🏠
              </div>
              <h3 style={{
                margin: 0,
                fontSize: '20px',
                fontWeight: '600',
                color: '#0a0a0a'
              }}>
                Apply for Work From Home
              </h3>
            </div>
            <button
              onClick={() => setShowModal(false)}
              style={{
                background: 'none',
                border: 'none',
                fontSize: '24px',
                color: '#9ca3af',
                cursor: 'pointer',
                padding: '4px',
                lineHeight: 1
              }}
            >
              ×
            </button>
          </div>

          {/* Body */}
          <div style={{ flex: 1, padding: '24px' }}>
            <p style={{
              color: '#6b7280',
              fontSize: '14px',
              marginBottom: '24px',
              lineHeight: '1.6'
            }}>
              Submit your work from home request by filling out the form below. Your manager will be notified for approval.
            </p>

            <Form onSubmit={handleSubmit}>
              <Form.Group className="mb-4">
                <Form.Label style={{
                  fontSize: '14px',
                  fontWeight: '600',
                  color: '#374151',
                  marginBottom: '8px',
                  display: 'block'
                }}>
                  Start Date
                </Form.Label>
                <Form.Control
                  required
                  type="date"
                  value={formData.start_date}
                  onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                  style={{
                    borderRadius: '8px',
                    border: '1px solid #e5e7eb',
                    padding: '12px 14px',
                    fontSize: '14px',
                    transition: 'border-color 0.2s ease'
                  }}
                />
              </Form.Group>

              <Form.Group className="mb-4">
                <Form.Label style={{
                  fontSize: '14px',
                  fontWeight: '600',
                  color: '#374151',
                  marginBottom: '8px',
                  display: 'block'
                }}>
                  End Date
                </Form.Label>
                <Form.Control
                  required
                  type="date"
                  value={formData.end_date}
                  onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                  style={{
                    borderRadius: '8px',
                    border: '1px solid #e5e7eb',
                    padding: '12px 14px',
                    fontSize: '14px',
                    transition: 'border-color 0.2s ease'
                  }}
                />
              </Form.Group>

              <Form.Group className="mb-4">
                <Form.Label style={{
                  fontSize: '14px',
                  fontWeight: '600',
                  color: '#374151',
                  marginBottom: '8px',
                  display: 'block'
                }}>
                  Reason
                </Form.Label>
                <ReactQuill
                  theme="snow"
                  value={formData.reason}
                  onChange={(value) => setFormData({ ...formData, reason: value })}
                  placeholder="Explain the reason for your WFH request..."
                  style={{
                    borderRadius: '8px',
                    border: '1px solid #e5e7eb',
                    backgroundColor: 'white'
                  }}
                  modules={{
                    toolbar: [
                      ['bold', 'italic', 'underline'],
                      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
                      ['clean']
                    ]
                  }}
                />
              </Form.Group>

              <div style={{
                display: 'flex',
                gap: '12px',
                paddingTop: '16px',
                borderTop: '1px solid #e5e7eb',
                marginTop: '24px'
              }}>
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  style={{
                    flex: 1,
                    padding: '12px 20px',
                    borderRadius: '8px',
                    backgroundColor: '#f3f4f6',
                    color: '#374151',
                    border: '1px solid #d1d5db',
                    fontSize: '14px',
                    fontWeight: '500',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease'
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  style={{
                    flex: 1,
                    padding: '12px 20px',
                    borderRadius: '8px',
                    backgroundColor: '#635bff',
                    color: 'white',
                    border: 'none',
                    fontSize: '14px',
                    fontWeight: '500',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease'
                  }}
                >
                  Submit Request
                </button>
              </div>
            </Form>
          </div>
        </div>
      </>
    </>
  );
};

export default WorkFromHome;
